import { Observable, from, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IWorkbookV2Service } from './iWorkbookV2.service';
import { WorkbookLog } from '../models/models';
import { IOfflineService } from './iOffline.service';
import { IOfflineWorkbookService } from './iOfflineWorkbook.service';
import { IWorkbookQueueEntry, Operation } from './offlineworkbook.service';
import moment from 'moment';

@Injectable()
export class WorkbookV2Service extends IWorkbookV2Service {
    constructor(private _http: HttpClient, private _offlineWorkbook: IOfflineWorkbookService, private _offlineService: IOfflineService) {
        super();
        this._offlineService.isOfflineObservable().subscribe(offline => {
            if (!offline) {
                this.syncOfflineQueue();
            }
        });
        // todo: merge this method and the one above
        this._offlineService.isOffline().subscribe(offline => {
            if (!offline) {
                this.syncOfflineQueue();
            }
        });
    }
    _operations: Subscription[] = [];
    private syncOfflineQueue() {
        this._offlineWorkbook.getQueueEntries(null).then(entries => {
            entries.forEach(entry => {
                switch (entry.operation) {
                    case Operation.Update:
                        // tslint:disable-next-line:no-console
                        console.info(`updating offline item`, entry);
                        // we are adding to operations here because if don't hold a reference the obserable will not be called
                        this._operations.push(this._http.post(`/api/Workbook`, entry.workbookLog).subscribe(() => {
                            // tslint:disable-next-line:no-console
                            console.debug('updated');
                            this._offlineWorkbook.removeQueuedEntry(entry);
                        }
                        , e => console.error(e)));
                        break;
                    case Operation.Delete:
                        // tslint:disable-next-line:no-console
                        console.info(`updating offline item`, entry);
                        // we are adding to operations here because if don't hold a reference the obserable will not be called
                        this._operations.push(this._http.delete(`/api/Workbook/${entry.id}`).subscribe(() => {
                            // tslint:disable-next-line:no-console
                            console.debug('run offline delete');
                            this._offlineWorkbook.removeQueuedEntry(entry);
                        }
                        , e => console.error(e)));
                        
                        break;
                }
            });
        });
    }
    get(jobId: string): Observable<WorkbookLog[]> {
        // merge offline queue
        return this._http.get<WorkbookLog[]>(`/api/Workbook/GetByJobId/${jobId}`).pipe(switchMap(async logs => {
            const apiLogs = logs ?? [];
            console.info('[WorkbookV2Service] workbook logs loaded', { jobId, workbookRows: apiLogs.length });
            try {
                const entries = await this.getQueueEntries(jobId);
                const filteredLogs = apiLogs.filter(l => entries.every(e => e.id !== l.id));
                entries.forEach(entry => {
                    switch (entry.operation) {
                        case Operation.Insert:
                        case Operation.Update:
                            if (entry.workbookLog) {
                                filteredLogs.push(entry.workbookLog);
                            }
                            break;
                        case Operation.Delete:
                            break;
                    }
                });
                filteredLogs.sort((a, b) => this.getTimeValue(b) - this.getTimeValue(a));
                console.info('[WorkbookV2Service] workbook logs ready', {
                    jobId,
                    workbookRows: filteredLogs.length,
                    offlineQueueEntries: entries.length
                });
                return filteredLogs;
            } catch (error) {
                console.warn('[WorkbookV2Service] workbook offline merge failed; using workbook API rows only', {
                    jobId,
                    workbookRows: apiLogs.length,
                    error
                });
                return apiLogs;
            }
        }
        ));
    }

    private getTimeValue(log: WorkbookLog): number {
        const time = log?.Time;
        if (!time) {
            return 0;
        }

        if (time instanceof Date) {
            return time.valueOf();
        }

        const parsed = moment(time);
        return parsed.isValid() ? parsed.valueOf() : 0;
    }

    private async getQueueEntries(jobId: string): Promise<IWorkbookQueueEntry[]> {
        try {
            const entries = await this.withTimeout(
                this._offlineWorkbook.getQueueEntries(jobId),
                5000,
                `Timed out loading offline workbook queue for job ${jobId}`
            );
            console.info('[WorkbookV2Service] offline queue entries loaded', { jobId, offlineQueueEntries: entries.length });
            return entries;
        } catch (error) {
            console.warn('[WorkbookV2Service] offline queue unavailable; using workbook API rows only', { jobId, error });
            return [];
        }
    }

    private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
        let timeoutHandle: ReturnType<typeof setTimeout>;
        const timeout = new Promise<T>((_, reject) => {
            timeoutHandle = setTimeout(() => reject(new Error(message)), timeoutMs);
        });

        return Promise.race([
            promise.finally(() => clearTimeout(timeoutHandle)),
            timeout
        ]);
    }

    addOrUpdate(workbookLogToUpdate: WorkbookLog, allJobWorkbookLogs: WorkbookLog[]): Observable<any> {
        return new Observable<any>(observer => {
            // No longer storing the Relative time server side and only calculating the relative time on load
            // so that multiple updates do not need to happen when RIH-Start is changed.
            // const affectedLogs = this.calculateRelativeTime(workbookLogToUpdate, allJobWorkbookLogs);
            const affectedLogs: WorkbookLog[] = [ workbookLogToUpdate ];
            this.addOrUpdateBatch(affectedLogs).subscribe(() => {
                observer.next(undefined);
                observer.complete();
            });
        });
    }

    delete(log: WorkbookLog): Observable<any> {
        return this._offlineService.isOffline().pipe(switchMap(offline => {
            if (offline) {
                return from(this._offlineWorkbook.queueDelete(log));
            } else {
                return this._http.delete(`/api/Workbook/${log.id}`);
            }
        }));
    }

    calculateRelativeTime(workbookLogToUpdate: WorkbookLog, allJobWorkbookLogs: WorkbookLog[]): WorkbookLog[] {
        // If Workbook Log being added or modified has activity "RIH-Start"
        // Then the relativeTimes for each workbook row need to be updated
        if (workbookLogToUpdate.activity === 'RIH-Start') {
            const rihStartDate = moment(workbookLogToUpdate.Time);
            allJobWorkbookLogs.forEach(log => {
                const currentTime = moment(log.Time);
                log.relativeTime = moment.duration(currentTime.diff(rihStartDate)).asHours();
            });
            return allJobWorkbookLogs;
        }

        // If Workbook Log does not have "RIH-Start" actvity
        // Then calculate relative Time for this one row.
        //  - If there are not yet any "RIH-Start" events, relative Time is 0
        workbookLogToUpdate.relativeTime = 0;
        const foundRihStartEvent = allJobWorkbookLogs.find(log => log.activity === 'RIH-Start');
        if (foundRihStartEvent) {
            const rihStartDate = moment(foundRihStartEvent.Time);
            const currentTime = moment(workbookLogToUpdate.Time);
            workbookLogToUpdate.relativeTime = moment.duration(currentTime.diff(rihStartDate)).asHours();
        }
        return [workbookLogToUpdate];
    }

    calculateRelativeTimeValue(workbookLogToUpdate: WorkbookLog, allJobWorkbookLogs: WorkbookLog[]): number {
        if (workbookLogToUpdate.activity === 'RIH-Start') {
          return 0;
        } else {
          const foundRihStartEvent = allJobWorkbookLogs.find(log => log.activity === 'RIH-Start');
          if (foundRihStartEvent) {
            const rihStartDate = moment(foundRihStartEvent.Time);
            const currentTime = moment(workbookLogToUpdate.Time);
            return moment.duration(currentTime.diff(rihStartDate)).asHours();
        }
      }
      return 0;
    }

    addOrUpdateSingle(workbookLogToUpdate: WorkbookLog): Observable<any> {
        return this._offlineService.isOffline().pipe(switchMap(offline => {
            if (offline) {
                // tslint:disable-next-line:no-console
                console.info(`offline so queueing`, workbookLogToUpdate);
                return from(this._offlineWorkbook.queueUpdate(workbookLogToUpdate));
            } else {
                return this._http.post(`/api/Workbook`, workbookLogToUpdate);
            }
        }));
    }

    addOrUpdateBatch(workbookLogsToUpdate: WorkbookLog[]): Observable<any> {
        return new Observable<any>(observer => {
            const numberOfUpdatesRequired = workbookLogsToUpdate.length;
            let updatesCount = 0;
            workbookLogsToUpdate.forEach(log => {
                this.addOrUpdateSingle(log).subscribe(() => {
                    updatesCount++;
                    if (numberOfUpdatesRequired === updatesCount) {
                        observer.next(undefined);
                        observer.complete();
                    }
                });
            });
        });
    }

}
